import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Link2, X, ChevronLeft, Star } from 'lucide-react';
import { API, withCreds, smallInputCls, uploadImage, errMsg } from './AdminUI';

let uploadsConfigured = null; // cached per page load

export default function ImageManager({ images = [], onChange, multiple = true, subfolder = 'products' }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(0);
  const [canUpload, setCanUpload] = useState(uploadsConfigured);
  const fileRef = useRef(null);

  useEffect(() => {
    if (uploadsConfigured !== null) return;
    axios.get(`${API}/admin/uploads/status`, withCreds)
      .then(r => { uploadsConfigured = r.data.configured; setCanUpload(r.data.configured); })
      .catch(() => setCanUpload(false));
  }, []);

  const set = (next) => onChange(multiple ? next : next.slice(-1));

  const addUrl = () => {
    const v = url.trim();
    if (!v) return;
    set([...images, v]);
    setUrl('');
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(n => n + list.length);
    const uploaded = [];
    for (const f of list) {
      try { uploaded.push(await uploadImage(f, subfolder)); }
      catch (e) { alert(`${f.name}: ${errMsg(e, 'Upload failed')}`); }
      finally { setBusy(n => n - 1); }
    }
    if (uploaded.length) set([...images, ...uploaded]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const move = (i) => {
    if (i === 0) return;
    const next = [...images];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    onChange(next);
  };

  return (
    <div
      onDragOver={e => { if (canUpload) e.preventDefault(); }}
      onDrop={e => { if (canUpload) { e.preventDefault(); handleFiles(e.dataTransfer.files); } }}>
      <div className="flex flex-wrap gap-2 mb-2">
        {canUpload && (
          <>
            <input ref={fileRef} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={e => handleFiles(e.target.files)} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy > 0}
              className="flex items-center gap-2 px-3 py-1.5 bg-honey text-black rounded-lg text-sm font-bold disabled:opacity-50">
              <Upload className="w-4 h-4" /> {busy > 0 ? `Uploading ${busy}…` : 'Upload'}
            </button>
          </>
        )}
        <div className="flex flex-1 min-w-[200px] gap-2">
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="…or paste an image URL"
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addUrl())} className={`${smallInputCls} flex-1`} />
          <button type="button" onClick={addUrl} className="px-3 py-1.5 bg-ink/[0.04] border border-ink/10 rounded-lg text-ink/75 hover:text-ink">
            <Link2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {canUpload === false && <p className="text-ink/45 text-xs mb-2">Image upload is off until Cloudinary is configured. Pasting URLs still works.</p>}
      {canUpload && <p className="text-ink/45 text-xs mb-2">Tip: drag image files onto this area to upload.</p>}
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div key={`${img}-${i}`} className={`relative group w-16 h-16 rounded-lg overflow-hidden border ${i === 0 ? 'border-honey/60' : 'border-ink/10'}`}>
            <img src={img} alt="" className="w-full h-full object-cover" />
            {i === 0 && multiple && <Star className="absolute top-0.5 left-0.5 w-3 h-3 text-honey-600 fill-honey" />}
            <div className="absolute inset-0 bg-ink/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1 transition-opacity">
              {i > 0 && (
                <button type="button" title="Move earlier" onClick={() => move(i)} className="p-1 bg-ink/[0.07] rounded text-ink"><ChevronLeft className="w-3 h-3" /></button>
              )}
              <button type="button" title="Remove" onClick={() => onChange(images.filter((_, j) => j !== i))} className="p-1 bg-red-500 rounded text-ink"><X className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>
      {multiple && images.length > 1 && <p className="text-ink/45 text-xs mt-1">The first image (starred) is the main product photo.</p>}
    </div>
  );
}
